import { getBlogNavInitialData } from '@/api/blog/blogNav';
import { getWordPressPostSlugs } from '@/api/posts/getWordpressPostsSlugs';
import { getPosts, getPostsDrafted } from '@/api/posts/posts';
import { getPostsRelated } from '@/api/posts/postsRelated';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { BlogLayout } from '@/components/layout/withBlogLayout';
import { NotFoundError } from '@/utils/handleSSRError';
import type { RelationFilter } from '@gik/api/posts/PostsUrlBuilder';
import { ArticlePageWithData } from '@gik/blog/components/ArticlePage/ArticlePage';
import type { StaticProps } from '@gik/core/api/BaseAPIConfig';
import { baseFallback } from '@gik/core/api/BaseAPIConfig';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import { SWRConfig } from 'swr';
import routes from '@gik/core/routes';

export default function PostDetailPage(props): React.ReactElement {
  const router = useRouter();
  // if (router.isFallback) {
  //   // TODO: wrap each page with withFallback function
  //   return <div>Loading...</div>;
  // }

  if (props.fallback.article?.[0].status == 'draft') {
    router.push(routes.blog);
    return null;
  }

  return (
    <SWRConfig value={{ fallback: props.fallback }}>
      <ArticlePageWithData slug={router.query.slug as string} />
    </SWRConfig>
  );
}

PostDetailPage.getLayout = function getLayout(page: React.ReactElement, layoutProps: PageLayoutProps) {
  return <BlogLayout {...layoutProps}>{page}</BlogLayout>;
};

async function _getStaticProps(context: GetServerSidePropsContext): Promise<StaticProps> {
  const slug = context.params.slug as string;

  let articleResponse = getPosts({
    slug,
    perPage: 1,
  });

  let article = await articleResponse.json;
  const { situationsResponse, tagsResponse } = getBlogNavInitialData();

  const [situations, tags] = await Promise.all([situationsResponse.json, tagsResponse.json]);

  if (!article?.length) {
    articleResponse = getPostsDrafted(slug);

    article = await articleResponse.json;

    if (article?.[0].status == 'draft') {
      return {
        redirect: {
          destination: routes.blog,
          permanent: true,
        },
      };
    }
  }

  if (!article?.length) {
    throw new NotFoundError();
  }

  const relatedArticlesResponse = article.length
    ? getPostsRelated({
        relationFilters: [
          {
            taxonomy: article?.[0]?.['primary-taxonomy-meta']?.taxonomy,
            terms: [article?.[0]?.['primary-taxonomy-meta']?.slug],
          },
        ] as RelationFilter<string[]>[],
      })
    : null;

  return baseFallback(
    {
      [articleResponse.key]: article,
      [situationsResponse.key]: situations,
      [tagsResponse.key]: tags,
      ...(article.length
        ? {
            [relatedArticlesResponse.key]: await relatedArticlesResponse.json,
          }
        : {}),
    },
    {
      articleKey: articleResponse.key,
      relatedArticlesKey: article.length ? relatedArticlesResponse.key : null,
    }
  );
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps));

export async function getStaticPaths() {
  if (process.env.RENDER_STATIC_PAGES_AT_BUILD_TIME === 'true') {
    const articles = await getWordPressPostSlugs();
    const paths = articles.map(({ slug }) => ({
      params: { slug },
    }));
    return { paths, fallback: true };
  } else {
    return { paths: [], fallback: 'blocking' };
  }
}
